/**
 * Frontend Chunk protocol types and helpers.
 *
 * Mirrors the backend's CanvasChunkType enum and CanvasChunk model
 * (backend/chunk_protocol.py) so the frontend can parse and construct
 * Chunk-wrapped WebSocket messages.
 *
 * This module is self-contained -- no imports from other modules.
 */

// ---------------------------------------------------------------------------
// CanvasChunkType enum -- mirrors backend CanvasChunkType exactly
// ---------------------------------------------------------------------------

export enum CanvasChunkType {
  CANVAS_ACTION = "CANVAS_ACTION",
  CANVAS_CONTEXT = "CANVAS_CONTEXT",
  CANVAS_TEXT = "CANVAS_TEXT",
  /** Outbound: canvas session chat history sent to gateway on canvas close or page unload. */
  CANVAS_SESSION_MESSAGES = "CANVAS_SESSION_MESSAGES",
  /** Inbound: signals that the gateway has started processing the turn (mirrors tutor START chunk). */
  CANVAS_START = "CANVAS_START",
  CANVAS_DONE = "CANVAS_DONE",
  CANVAS_ERROR = "CANVAS_ERROR",
  /** Inbound: per-turn gateway latency metrics for STT, LLM, and TTS. */
  CANVAS_METRICS = "CANVAS_METRICS",
  /** Outbound: base64-encoded PCM audio chunk sent to gateway when canvas is open. */
  CANVAS_AUDIO = "CANVAS_AUDIO",
  /** Outbound: signals end of PCM audio stream to gateway (triggers STT finalization). */
  CANVAS_AUDIO_DONE = "CANVAS_AUDIO_DONE",
  /** Inbound: one fully-timestamped caption Token per chunk. Mirrors the ai-tutor backend's
   *  CAPTIONS_UPDATE schema exactly so the canvas agent and tutor share one frontend renderer.
   *  chunk_data is JSON of `{caption_text, is_math, flush, words: [{text, start_ms, duration_ms}]}`. */
  CANVAS_CAPTIONS_UPDATE = "CANVAS_CAPTIONS_UPDATE",
  /** Inbound: user's transcribed speech from STT, sent for chat history storage. */
  CANVAS_USER_TRANSCRIPT = "CANVAS_USER_TRANSCRIPT",
  /** Inbound: transcriptions of uploaded-image data URIs for the turn. The frontend
   *  swaps them for the data URIs in its chat history. */
  CANVAS_IMAGE_TRANSCRIPTIONS = "CANVAS_IMAGE_TRANSCRIPTIONS",
  /** Outbound: a confirmed paper photo; the gateway OCRs it and replies with CANVAS_INJECT. */
  CANVAS_PAGE_CAPTURE = "CANVAS_PAGE_CAPTURE",
  /** Inbound: out-of-turn paper OCR actions, handled at connection level since no turn listener exists. */
  CANVAS_INJECT = "CANVAS_INJECT",
  /**
   * Outbound: client-measured latency the server can't see directly, e.g.
   * spacebar-release → first CANVAS_AUDIO chunk. Logged to the gateway's
   * local JSONL metrics sink.
   */
  CANVAS_CLIENT_METRIC = "CANVAS_CLIENT_METRIC",
  /** Inbound: connection-level seed actions sent at WS open for notepad bites.
   *  chunk_data is JSON of `{actions: [...]}` — create/pen actions the frontend
   *  applies to the canvas before the opening "start teaching" turn. Arrives
   *  outside any turn (synthetic turn_id), handled at the connection level. */
  CANVAS_SEED = "CANVAS_SEED",
  /** Inbound: connection-level preprocessing complete. Sent once per connection
   *  after the gateway finishes any pre-turn work (e.g. seeding). The frontend
   *  gates the opening "start teaching" call on this. chunk_data is JSON of
   *  `{preprocessed: boolean, fragment_count: number}`. */
  CANVAS_READY = "CANVAS_READY",
}

// ---------------------------------------------------------------------------
// CanvasChunk interface -- mirrors backend CanvasChunk Pydantic model
// ---------------------------------------------------------------------------

/**
 * Chunk envelope for all canvas WebSocket data messages.
 * Control messages (ping/pong/cancel) remain bare JSON, not wrapped.
 *
 * chunk_data is always a JSON string on the frontend (not bytes/List[int])
 * because canvas chunks use JSON-serialized structured payloads.
 */
export interface CanvasChunk {
  chunk_type: CanvasChunkType;
  chunk_data: string;
  turn_id: string;
  timestamp: number;
}

// ---------------------------------------------------------------------------
// Typed chunk_data interfaces (matching backend schemas)
// ---------------------------------------------------------------------------

/** chunk_data payload for CANVAS_TEXT chunks (outbound user message). */
export interface CanvasTextChunkData {
  user_message: string;
  /** Original agentMessages array — preserves non-text items (e.g. data:image/) for the gateway. */
  agent_messages?: string[];
  /** Who authored this turn's message. The gateway needs it because a
   *  frontend-issued prompt ("Start teaching…", the revisit nudge) reaches it
   *  as an ordinary user_message and must not be mistaken for the student
   *  speaking. Values mirror AgentRequest["source"] — inlined because this
   *  module stays import-free (see header). */
  prompt_source?: "user" | "self" | "other-agent";
}

/** chunk_data payload for CANVAS_SESSION_MESSAGES chunks (outbound canvas-close chat history). */
export interface CanvasSessionSummaryChunkData {
  chat_history: Record<string, unknown>[];
}

/** chunk_data payload for CANVAS_ERROR chunks (inbound error). */
export interface CanvasErrorChunkData {
  error: string;
  code?: string;
}

/** chunk_data payload for CANVAS_AUDIO chunks (outbound PCM audio). */
export interface CanvasAudioChunkData {
  /** Base64-encoded PCM audio — same format as tutor WS AUDIO chunks. */
  audio_chunk_b64: string;
}

/** chunk_data payload for CANVAS_IMAGE_TRANSCRIPTIONS chunks (inbound).
 *  image_replacements has one entry per `data:image/` URI in the order they appeared
 *  in this turn's agentMessages. A string is the transcription text; { failed } means
 *  transcription failed with a reason ("quality" = blank/dark image, "error" = API failure);
 *  null is a legacy sentinel (treated same as error). */
export interface CanvasImageTranscriptionsChunkData {
  image_replacements: (string | { failed: "quality" | "error" } | null)[];
}

/** chunk_data payload for CANVAS_PAGE_CAPTURE chunks (outbound, on upload confirm). */
export interface CanvasPageCaptureChunkData {
  /** data:image/...;base64,... of the confirmed (cropped) photo. */
  png: string;
  /** Echoed on the CANVAS_INJECT; the gateway claims images by content. */
  captureId: string;
  /** Stands in for the turn's `agentViewportBounds` part, absent before the first turn. */
  agentViewport?: { x: number; y: number; w: number; h: number };
  /** Re-claim after reconnect of a photo already injected: skip OCR, only claim. */
  digitized?: boolean;
}

/** chunk_data payload for CANVAS_INJECT chunks; mirrors the gateway's ws-schema type. */
export interface CanvasInjectChunkData {
  actions: Record<string, unknown>[];
  source: "page_ocr";
  captureId: string;
}

/** chunk_data payload for CANVAS_SEED chunks (inbound connection-level seed actions). */
export interface CanvasSeedChunkData {
  actions: Record<string, unknown>[];
}

/** chunk_data payload for CANVAS_READY chunks (inbound preprocessing-complete signal). */
export interface CanvasReadyChunkData {
  preprocessed: boolean;
  fragment_count: number;
}

/** chunk_data payload for CANVAS_METRICS chunks (inbound latency instrumentation). */
export interface CanvasMetricsChunkData {
  stage: "stt" | "llm" | "tts" | "cancel" | "done";
  collectedAt: number;
  handoff?: {
    latencyMs: number;
  } | null;
  stt?: {
    latencyMs: number;
  } | null;
  llm?: {
    firstActionMs: number | null;
    totalMs: number | null;
    actionCount: number | null;
    modelName?: string | null;
  } | null;
  tts?: {
    firstAudioMs: number | null;
    totalMs: number | null;
    chunkCount: number | null;
  } | null;
  cancel?: {
    stopMs: number | null;
  } | null;
}

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/**
 * Factory function for creating outbound Chunk envelopes.
 * Timestamp is set to Date.now() (milliseconds since epoch).
 */
export function makeCanvasChunk(
  chunkType: CanvasChunkType,
  chunkData: string = "",
  turnId: string = "",
): CanvasChunk {
  return {
    chunk_type: chunkType,
    chunk_data: chunkData,
    turn_id: turnId,
    timestamp: Date.now(),
  };
}

/**
 * Type guard for identifying incoming Chunk messages.
 * Returns true if the parsed JSON object has a chunk_type field
 * starting with "CANVAS_".
 */
export function isCanvasChunk(data: unknown): data is CanvasChunk {
  return (
    typeof data === "object" &&
    data !== null &&
    "chunk_type" in data &&
    typeof (data as Record<string, unknown>).chunk_type === "string" &&
    ((data as Record<string, unknown>).chunk_type as string).startsWith(
      "CANVAS_",
    )
  );
}
